import { dayjs } from '@2wunder/klarx-tool'
import { Col, Row } from 'antd'
import CategoryAttributesFormList from 'app/components/Core/CategoryAttribute/FormList'
import KForm from 'app/components/Modules/Form/_KForm'
import KDateRange from 'app/components/Modules/Form/KDateRange'
import KTextArea from 'app/components/Modules/Form/KTextArea'
import { FormRecord } from 'app/components/Modules/Form/typing'
import { normalizeLineItem } from 'app/lib/apollo/normalizers/LineItem'
import { convertStringToIso } from 'app/lib/apollo/normalizers/utilities'
import { useCurrentCustomer } from 'app/lib/hooks/useCurrent'
import useTranslation from 'app/lib/hooks/useTranslation'
import { RecordTypeEnum } from 'app/queries'
import { omit } from 'lodash'
import React, { ComponentProps } from 'react'
import { useIntl } from 'react-intl'

import MinimumRentalTimeFormItem from '../CategorySegment/Form/MinimumRentalTimeFormItem'
import { MemberProps } from '../convention'
import GuaranteedFormItem from './Form/GuaranteedFormItem'

type Props = {
  showAttributeValues?: boolean
} & Partial<ComponentProps<typeof KForm>>

const Form: React.FC<MemberProps<Partial<ILineItem>, Props>> = ({
  member,
  children,
  props: { onSuccess, portalId, mutationOptions = {}, showAttributeValues = true, ...others } = {}
}) => {
  const { tRecordType } = useTranslation({ recordType: RecordTypeEnum.lineItem })
  const { earliest_rental_start_date } = useCurrentCustomer()
  const { formatMessage } = useIntl()

  const payloadNormalizer = (values: FormRecord) => {
    const { category_attributes, start_time, return_time, startDate, returnDate, ...payload } =
      omit(values, 'dates')

    let startTime = null
    let returnTime = null

    if (isTimeAvailable) {
      startTime = start_time || startDate.split(' ')[1]
      returnTime = return_time || returnDate.split(' ')[1]
    }

    return {
      ...payload,
      startDate: convertStringToIso(startDate),
      returnDate: convertStringToIso(returnDate),
      startTime,
      returnTime,
      attributeValues: category_attributes?.map((i: ICategoryAttribute) => ({
        id: i.attribute_value.id,
        attributeDetailId: i.attribute_detail.id,
        displayValue: i.attribute_value.display_value
      }))
    }
  }

  const isTimeAvailable = member?.categorySegment?.unit === 'hours'

  const isSaturday = (date: dayjs.Dayjs) => date?.isValid() && date.day() === 6
  const isSunday = (date: dayjs.Dayjs) => date?.isValid() && date.day() === 0

  const weekendValidator = (_rule: unknown, value: dayjs.Dayjs[]) => {
    if (isSaturday(value?.[0])) {
      return Promise.reject(formatMessage({ id: 'line_item.form.time_period.start_date.error' }))
    } else if (isSunday(value?.[1])) {
      return Promise.reject(formatMessage({ id: 'line_item.form.time_period.return_date.error' }))
    }

    return Promise.resolve()
  }

  const getVisualAction = () => {
    if (member?.id) {
      return 'to_overview'
    } else if (member?.categorySegment?.hasAttachments) {
      return 'create_machine_with_attachment'
    } else {
      return 'create_machine_without_attachment'
    }
  }

  const startDate = member?.startDate || earliest_rental_start_date
  const returnDate = getReturnDate()

  function getReturnDate() {
    const { returnDate, categorySegment } = member || {}
    if (returnDate) return member.returnDate
    if (categorySegment?.minimumReturnDate)
      return dayjs(categorySegment?.minimumReturnDate).toLocaleDate()

    return startDate
  }

  return (
    <KForm
      mutationOptions={{
        silentSuccess: true,
        payloadNormalizer,
        ...mutationOptions
      }}
      portalId={portalId}
      initialFetch={false}
      recordId={member?.id}
      recordType={RecordTypeEnum.lineItem}
      initialValues={{
        ...member,
        category_attributes: member?.categorySegment?.category_attributes,
        startDate,
        returnDate,
        dates: [dayjs(startDate).parseLocaleDate(), dayjs(returnDate).parseLocaleDate()]
      }}
      visualAction={getVisualAction()}
      layout="vertical"
      onSuccess={(lineItem) => onSuccess(normalizeLineItem(lineItem))}
      {...others}>
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <Row>
            <Col>
              <KDateRange
                name="dates"
                startName="startDate"
                endName="returnDate"
                disablePast
                rules={[{ validator: weekendValidator }]}
                minDate={earliest_rental_start_date}
                showTime={isTimeAvailable}
                labelRenderer={(label) => <h3 className="text-black">{label}</h3>}
              />
            </Col>
            <If condition={member?.categorySegment?.minimumRentalDuration}>
              <Col span={24}>
                <MinimumRentalTimeFormItem member={member.categorySegment} />
              </Col>
            </If>
            <Col span={24}>
              <GuaranteedFormItem
                member={{
                  earliestAvailableDateTime:
                    member?.earliestAvailableDateTime ||
                    member?.categorySegment?.earliestAvailableDateTime
                }}
              />
            </Col>
          </Row>
        </Col>
        <If condition={showAttributeValues}>
          <Col span={24}>
            <Row>
              <If condition={member?.categorySegment?.category_attributes?.length}>
                <Col span={24}>
                  <h4 className="text-black">{tRecordType({ id: 'requirements' })}</h4>
                  <CategoryAttributesFormList />
                </Col>
              </If>
              <Col span={24}>
                <KTextArea name="annotation" />
              </Col>
            </Row>
          </Col>
        </If>
      </Row>
      {children}
    </KForm>
  )
}

export default Form
